import { useState, useEffect, useCallback } from 'react';
import { Icon, Spinner } from '../ui/Icon.js';
import { Button } from '../ui/Button.js';
import { apiGet } from '../../lib/api.js';
import { apiPost, apiDelete } from '../../lib/api-mutations.js';
import { useToast } from '../../hooks/useToast.js';
import { useBrand } from '../../lib/brand-context.js';
import type { Brand } from '../../types/index.js';

interface CompetitorItem {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  linked_at: string;
}

interface CompetitorListResponse {
  data: CompetitorItem[];
  total: number;
}

interface CompetitorSuggestion {
  name: string;
  mention_count: number;
  sample_snippet: string | null;
}

interface SuggestionListResponse {
  data: CompetitorSuggestion[];
}

interface SERPCompetitor {
  domain: string;
  name_guess: string;
  avg_position: number;
  keywords_count: number;
  shared_keywords: number;
  already_in_system: boolean;
  already_linked: boolean;
  brand_id: string | null;
}

interface SERPDiscoverResponse {
  data: SERPCompetitor[];
  mock: boolean;
}

export function CompetitorManager({ tenantId }: { tenantId: string }) {
  const { addToast } = useToast();
  const { selectedBrand, brands } = useBrand();
  const [competitors, setCompetitors] = useState<CompetitorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompetitorId, setSelectedCompetitorId] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<CompetitorSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [linkingSuggestion, setLinkingSuggestion] = useState<string | null>(null);
  const [serpCompetitors, setSerpCompetitors] = useState<SERPCompetitor[]>([]);
  const [serpLoading, setSerpLoading] = useState(false);
  const [serpLinking, setSerpLinking] = useState<string | null>(null);

  const brandId = selectedBrand?.id;

  const loadCompetitors = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await apiGet<CompetitorListResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/competitors`,
      );
      setCompetitors(res.data);
    } catch {
      addToast('Failed to load competitors', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, brandId, addToast]);

  const loadSuggestions = useCallback(async () => {
    if (!brandId) return;
    setSuggestionsLoading(true);
    try {
      const res = await apiGet<SuggestionListResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/competitors/suggestions`,
      );
      setSuggestions(res.data);
    } catch {
      // Non-critical
    } finally {
      setSuggestionsLoading(false);
    }
  }, [tenantId, brandId]);

  useEffect(() => {
    loadCompetitors();
    loadSuggestions();
  }, [loadCompetitors, loadSuggestions]);

  // Available brands to link (exclude current brand and already-linked competitors)
  const competitorIds = new Set(competitors.map((c) => c.id));
  const availableBrands = brands.filter(
    (b) => b.id !== brandId && !competitorIds.has(b.id),
  );

  async function handleLink() {
    if (!brandId || !selectedCompetitorId) return;
    setLinking(true);
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/brands/${brandId}/competitors`, {
        competitor_brand_id: selectedCompetitorId,
      });
      addToast('Competitor linked successfully', 'success');
      setSelectedCompetitorId('');
      await loadCompetitors();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to link competitor', 'error');
    } finally {
      setLinking(false);
    }
  }

  async function handleUnlink(competitorBrandId: string) {
    if (!brandId) return;
    setUnlinking(competitorBrandId);
    try {
      await apiDelete(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/competitors/${competitorBrandId}`,
      );
      addToast('Competitor unlinked', 'success');
      setCompetitors((prev) => prev.filter((c) => c.id !== competitorBrandId));
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to unlink competitor', 'error');
    } finally {
      setUnlinking(null);
    }
  }

  async function handleLinkSuggestion(name: string) {
    if (!brandId) return;
    setLinkingSuggestion(name);
    try {
      // First, create the brand if it doesn't exist
      const createRes = await apiPost<{ data: { id: string } }>(
        `/api/v1/tenants/${tenantId}/brands`,
        { name },
      );
      const newBrandId = createRes.data.id;
      // Then link as competitor
      await apiPost(`/api/v1/tenants/${tenantId}/brands/${brandId}/competitors`, {
        competitor_brand_id: newBrandId,
      });
      addToast(`${name} linked as competitor`, 'success');
      await loadCompetitors();
      await loadSuggestions();
    } catch (err) {
      // If brand already exists, try to find it and link
      try {
        const brandsRes = await apiGet<{ data: Brand[] }>(
          `/api/v1/tenants/${tenantId}/brands?search=${encodeURIComponent(name)}&limit=1`,
        );
        if (brandsRes.data.length > 0) {
          await apiPost(`/api/v1/tenants/${tenantId}/brands/${brandId}/competitors`, {
            competitor_brand_id: brandsRes.data[0].id,
          });
          addToast(`${name} linked as competitor`, 'success');
          await loadCompetitors();
          await loadSuggestions();
          return;
        }
      } catch { /* fallthrough */ }
      addToast(err instanceof Error ? err.message : `Failed to link ${name}`, 'error');
    } finally {
      setLinkingSuggestion(null);
    }
  }

  async function handleDiscoverSERP() {
    if (!brandId) return;
    setSerpLoading(true);
    try {
      const res = await apiGet<SERPDiscoverResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/competitors/discover`,
      );
      setSerpCompetitors(res.data);
      if (res.data.length === 0) {
        addToast('No SERP competitors found. Ensure the brand has a domain set.', 'success');
      }
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to discover SERP competitors', 'error');
    } finally {
      setSerpLoading(false);
    }
  }

  async function handleSerpCreateAndLink(comp: SERPCompetitor) {
    if (!brandId) return;
    setSerpLinking(comp.domain);
    try {
      if (comp.already_in_system && comp.brand_id) {
        // Just link
        await apiPost(`/api/v1/tenants/${tenantId}/brands/${brandId}/competitors`, {
          competitor_brand_id: comp.brand_id,
        });
      } else {
        // Create brand, then link
        const createRes = await apiPost<{ data: { id: string } }>(
          `/api/v1/tenants/${tenantId}/brands`,
          { name: comp.name_guess, domain: comp.domain },
        );
        await apiPost(`/api/v1/tenants/${tenantId}/brands/${brandId}/competitors`, {
          competitor_brand_id: createRes.data.id,
        });
      }
      addToast(`${comp.name_guess} linked as competitor`, 'success');
      await loadCompetitors();
      // Remove from SERP list
      setSerpCompetitors(prev => prev.map(c =>
        c.domain === comp.domain ? { ...c, already_linked: true } : c
      ));
    } catch (err) {
      addToast(err instanceof Error ? err.message : `Failed to link ${comp.name_guess}`, 'error');
    } finally {
      setSerpLinking(null);
    }
  }

  if (!selectedBrand) {
    return (
      <div className="hub-plate hub-plate--flat py-12 text-center">
        <p className="text-sm text-gray-500">Select a brand from the header to manage competitors.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Managing competitors for <span className="font-medium text-gray-700">{selectedBrand.name}</span>
      </p>

      {/* Link competitor */}
      <div className="mb-6 flex items-center gap-3">
        <select
          value={selectedCompetitorId}
          onChange={(e) => setSelectedCompetitorId(e.target.value)}
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:outline-none"
        >
          <option value="">Select a brand to link as competitor...</option>
          {availableBrands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name} {b.domain ? `(${b.domain})` : ''}
            </option>
          ))}
        </select>
        <Button variant="primary" onClick={handleLink} disabled={!selectedCompetitorId || linking}>
          {linking ? <Spinner /> : <Icon name="bx-link-alt" />}
          Link
        </Button>
      </div>

      {/* Competitor list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="text-gray-400" />
        </div>
      ) : competitors.length === 0 ? (
        <div className="hub-plate hub-plate--flat py-12 text-center">
          <p className="text-sm text-gray-500">No competitors linked. Use the dropdown above to link one.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="hub-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Industry</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {competitors.map((comp) => (
                <tr key={comp.id}>
                  <td className="font-medium">{comp.name}</td>
                  <td className="text-[var(--hub-color-ink-muted)]">{comp.domain ?? '-'}</td>
                  <td className="text-[var(--hub-color-ink-muted)]">{comp.industry ?? '-'}</td>
                  <td className="text-right">
                    <button
                      onClick={() => handleUnlink(comp.id)}
                      disabled={unlinking === comp.id}
                      className="hub-btn hub-btn--quiet"
                      title="Unlink competitor"
                    >
                      {unlinking === comp.id ? (
                        <Spinner />
                      ) : (
                        <Icon name="bx-unlink" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Suggested Competitors (auto-detected from AI responses) */}
      <div className="mt-8" data-testid="competitor-suggestions">
        <div className="flex items-center gap-2">
          <Icon name="bx-star" className="text-violet-500" />
          <h3 className="text-sm font-semibold text-slate-900">Detected in responses</h3>
        </div>
        {suggestionsLoading ? (
          <div className="mt-3 flex justify-center">
            <Spinner className="text-slate-400" />
          </div>
        ) : suggestions.length === 0 ? (
          <p className="mt-2 text-xs text-slate-500">
            No new competitors detected in AI responses. Run more queries to discover competitors.
          </p>
        ) : (
          <>
            <p className="mt-1 text-xs text-slate-500">
              Based on AI responses, these brands are frequently mentioned alongside {selectedBrand.name}:
            </p>
            <div className="mt-3 space-y-2">
              {suggestions.map((s) => (
                <div
                  key={s.name}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3"
                  data-testid={`suggestion-${s.name}`}
                >
                  <div>
                    <span className="text-sm font-medium text-slate-900">{s.name}</span>
                    <span className="ml-2 text-xs text-slate-500">Mentioned {s.mention_count} time{s.mention_count !== 1 ? 's' : ''}</span>
                  </div>
                  <button
                    onClick={() => handleLinkSuggestion(s.name)}
                    disabled={linkingSuggestion === s.name}
                    className="hub-btn hub-btn--ghost"
                    data-testid={`link-suggestion-${s.name}`}
                  >
                    {linkingSuggestion === s.name ? (
                      <Spinner  />
                    ) : (
                      <Icon name="bx-link-alt" />
                    )}
                    Create &amp; link
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {/* SERP Competitors (DataForSEO discovery) */}
      <div className="mt-8" data-testid="serp-competitors">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="bx-search" className="text-violet-500" />
            <h3 className="text-sm font-semibold text-slate-900">SERP competitors</h3>
          </div>
          <Button variant="ghost" onClick={handleDiscoverSERP} disabled={serpLoading || !selectedBrand?.domain} data-testid="discover-serp-btn">
            {serpLoading ? <Spinner /> : <Icon name="bx-search" />}
            Discover from SERP
          </Button>
        </div>
        {!selectedBrand?.domain && (
          <p className="mt-2 text-xs text-slate-500">
            Set a domain for {selectedBrand.name} to discover SERP competitors.
          </p>
        )}
        {serpCompetitors.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="hub-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th className="is-num">Avg pos</th>
                  <th className="is-num">Keywords</th>
                  <th className="is-num">Shared</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {serpCompetitors.map(comp => (
                  <tr key={comp.domain}>
                    <td>
                      <div className="font-medium">{comp.name_guess}</div>
                      <div className="text-xs text-[var(--hub-color-ink-muted)]">{comp.domain}</div>
                    </td>
                    <td className="is-num">{comp.avg_position.toFixed(1)}</td>
                    <td className="is-num">{comp.keywords_count.toLocaleString()}</td>
                    <td className="is-num">{comp.shared_keywords}</td>
                    <td className="text-right">
                      {comp.already_linked ? (
                        <span className="hub-chip hub-chip--ok">Linked</span>
                      ) : (
                        <button
                          onClick={() => handleSerpCreateAndLink(comp)}
                          disabled={serpLinking === comp.domain}
                          className="hub-btn hub-btn--ghost"
                          data-testid={`serp-link-${comp.domain}`}
                        >
                          {serpLinking === comp.domain ? (
                            <Spinner />
                          ) : (
                            <Icon name="bx-link-alt" />
                          )}
                          Create &amp; link
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
